import React from 'react';
import { CircularProgress, Typography, Alert } from '@mui/material';
import StatsCard from './StatsCard';

/**
 * StatsCardsDashboard Component
 * 
 * A reusable dashboard that displays key metrics using the modern StatsCard component
 * 
 * @param {Object} props Component props
 * @param {string} props.title Dashboard title
 * @param {Array} props.metrics Array of metric objects to display
 * @param {boolean} props.loading Whether data is currently loading
 * @param {string} props.error Error message if any
 * @param {boolean} props.transparent Whether to make the background transparent
 * @returns {JSX.Element} StatsCardsDashboard component
 */
const StatsCardsDashboard = ({ 
  title, 
  metrics = [], 
  loading = false, 
  error = null,
  transparent = false 
}) => {
  const containerClass = transparent 
    ? "space-y-4 sm:space-y-6 mb-4 sm:mb-6" 
    : "space-y-4 sm:space-y-6 mb-4 sm:mb-6 bg-white p-4 sm:p-6 rounded-xl shadow-md";

  if (loading) {
    return (
      <div className={containerClass}>
        {title && (
          <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent mb-3 sm:mb-4">
            {title} Overview
          </h2>
        )}
        <div className="flex justify-center items-center h-40">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        {title && (
          <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent mb-3 sm:mb-4">
            {title} Overview
          </h2>
        )}
        <Alert severity="warning" className="mb-3 sm:mb-4">
          {error}
        </Alert>
        {/* 2-column grid layout (matching Expenses Overview style) */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {metrics.map((metric, index) => (
            <StatsCard
              key={index}
              title={metric.title}
              value={metric.value}
              description={metric.description}
              change={metric.change}
              isPositive={metric.isPositive}
              icon={metric.icon}
              color={metric.color || getDefaultColor(index)}
              tooltip={metric.tooltip}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {title && (
        <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-700 bg-clip-text text-transparent mb-3 sm:mb-4">
          {title} Overview
        </h2>
      )}
      {/* 2-column grid layout (matching Expenses Overview style) */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {metrics.map((metric, index) => (
          <StatsCard
            key={index}
            title={metric.title}
            value={metric.value}
            description={metric.description}
            change={metric.change}
            isPositive={metric.isPositive}
            icon={metric.icon}
            color={metric.color || getDefaultColor(index)}
          />
        ))}
      </div>
    </div>
  );
};

// Helper function to get default colors for metrics
const getDefaultColor = (index) => {
  const colors = ['indigo', 'green', 'red', 'blue', 'orange', 'emerald', 'purple'];
  return colors[index % colors.length];
};

export default StatsCardsDashboard; 